import type { FieldCatalogItem } from './form-field-catalog';
import type { BuiltForm, FieldType, FormField } from '../models/built-form';
import { DEFAULT_FORM_THEME_SHADES } from './form-theme-catalog';
import { defaultGridChoices } from './grid-choice-option';

/** Field types with real canvas/preview support today. */
const AVAILABLE_TYPES = new Set<FieldType>([
  'text',
  'textarea',
  'email',
  'phone',
  'number',
  'currency',
  'url',
  'select',
  'searchable_picker',
  'single_choice',
  'multi_choice',
  'single_choice_grid',
  'multi_choice_grid',
  'number_grid',
  'checkbox',
  'checkbox_group',
  'radio_group',
  'switch',
  'date',
  'camera',
  'image_picker',
  'address_selector',
  'map',
  'date_range',
  'slider',
  'rating',
  'range_slider',
  'heading',
  'paragraph',
  'divider',
  'spacer',
]);

/** On-canvas defaults when adding a field — independent of palette labels. */
const FIELD_SEED: Record<FieldType, { label: string; placeholder?: string }> = {
  heading: { label: 'Heading' },
  paragraph: { label: 'Paragraph' },
  divider: { label: 'Divider' },
  spacer: { label: 'Spacer' },
  text: { label: 'Short answer', placeholder: 'Type your answer' },
  textarea: { label: 'Your response', placeholder: 'Type your response' },
  email: { label: 'Email', placeholder: 'name@example.com' },
  phone: { label: 'Phone number', placeholder: 'Enter phone number' },
  number: { label: 'Number', placeholder: 'Enter a number' },
  currency: { label: 'Amount', placeholder: '0.00' },
  url: { label: 'Website', placeholder: 'https://' },
  checkbox: { label: 'I agree' },
  checkbox_group: { label: 'Select all that apply' },
  radio_group: { label: 'Choose one' },
  switch: { label: 'Enable this option' },
  select: { label: 'Select an option', placeholder: 'Choose…' },
  searchable_picker: { label: 'Search and select', placeholder: 'Search and select…' },
  single_choice: { label: 'Choose one' },
  multi_choice: { label: 'Choose your options' },
  single_choice_grid: { label: 'Choice grid' },
  multi_choice_grid: { label: 'Multi choice grid' },
  number_grid: { label: 'Number grid' },
  date: { label: 'Date', placeholder: 'Select a date' },
  time: { label: 'Time', placeholder: 'Select a time' },
  datetime: { label: 'Date and time', placeholder: 'Select date and time' },
  date_range: { label: 'Date range', placeholder: 'Select start and end dates' },
  time_range: { label: 'Time range', placeholder: 'Select start and end times' },
  slider: { label: 'Rating' },
  rating: { label: 'Rating' },
  range_slider: { label: 'Range' },
  color: { label: 'Color' },
  numeric_stepper: { label: 'Quantity' },
  image_picker: { label: 'Upload an image' },
  camera: { label: 'Take a photo' },
  live_geo_coordinates: { label: 'Your location' },
  address_selector: { label: 'Address', placeholder: 'Search for an address' },
  map: { label: 'Location', placeholder: 'Pick a location on the map' },
  file_uploader: { label: 'Upload a file' },
  audio_recorder: { label: 'Record audio' },
  video_recorder: { label: 'Record video' },
  rich_text: { label: 'Details', placeholder: 'Write here…' },
  document_scanner: { label: 'Scan a document' },
  drawing: { label: 'Drawing' },
  signature: { label: 'Signature' },
  captcha: { label: 'Verification' },
};

function nextFieldId(): string {
  return `f_${crypto.randomUUID().replace(/-/g, '').slice(0, 12)}`;
}

export function cloneFieldWithNewId(field: FormField): FormField {
  return {
    ...structuredClone(field),
    id: nextFieldId(),
  };
}

export function createFieldFromCatalog(item: FieldCatalogItem): FormField | null {
  if (!item.available || !AVAILABLE_TYPES.has(item.id as FieldType)) {
    return null;
  }

  const type = item.id as FieldType;
  const seed = FIELD_SEED[type];

  if (type === 'heading') {
    return {
      id: nextFieldId(),
      type,
      label: seed.label,
      headingLevel: 2,
    };
  }

  if (type === 'paragraph') {
    return {
      id: nextFieldId(),
      type,
      label: seed.label,
    };
  }

  if (type === 'divider') {
    return {
      id: nextFieldId(),
      type,
      label: seed.label,
    };
  }

  if (type === 'spacer') {
    return {
      id: nextFieldId(),
      type,
      label: seed.label,
      spacerSize: 'md',
    };
  }

  const field: FormField = {
    id: nextFieldId(),
    type,
    label: seed.label,
    placeholder: seed.placeholder,
  };

  if (
    type === 'select' ||
    type === 'radio_group' ||
    type === 'single_choice' ||
    type === 'multi_choice'
  ) {
    field.options = ['Choice 1', 'Choice 2', 'Choice 3'];
    field.orientation = 'vertical';
    field.radioSize = 'md';
  }

  if (type === 'currency') {
    field.helpText = 'Enter the amount';
    field.config = {
      defaultCurrency: null,
      allowChangeCurrency: true,
    };
  }

  if (type === 'rating') {
    field.helpText = 'Rate your experience';
    field.config = {
      max: 5,
    };
    field.validation = {
      min: 1,
      max: 5,
    };
  }


  if (type === 'multi_choice_grid') {
    field.config = {
      responseType: 'checkbox',
      options: defaultGridChoices(['Choice 1', 'Choice 2']),
      items: ['Item 1', 'Item 2', 'Item 3'],
    };
  }
  if (type === 'single_choice_grid') {
    field.helpText = 'Check each item and select the status.';
    field.options = ['Option 1', 'Option 2', 'Option 3'];
    field.config = {
      responseType: 'button',
      items: [
        'Option 1',
        'Option 2',
        'Option 3',
      ],
      choice: defaultGridChoices(['Yes', 'No']),
      allowAdditionalRows: false,
      addRowButtonLabel: '+ Add another item',
    };
  }

  if (type === 'number_grid') {
    field.config = {
      items: [{ display: 'Item 1' }, { display: 'Item 2' }, { display: 'Item 3' }],
      columns: defaultGridChoices(['Column 1']),
    };
  }
  return field;
}

export function createEmptyBuiltForm(): BuiltForm {
  return {
    title: 'Untitled form',
    inputAppearance: 'fill',
    themeColor: { ...DEFAULT_FORM_THEME_SHADES },
    pages: [
      {
        id: 'page_1',
        order: 1,
        cta: { primaryLabel: 'Submit' },
        fields: [],
      },
    ],
  };
}

export function isLayoutFieldType(type: FieldType): boolean {
  return type === 'heading' || type === 'paragraph' || type === 'divider' || type === 'spacer';
}
